import { action, computed, observable, ObservableMap, makeObservable } from 'mobx';
import moment, { Moment } from 'moment-timezone';
import { SessionType } from '../../../types/sessions';
import { ISODate } from '../../../../commonv3/types/dates';
import { RemoteResourceV3, updateRemoteResourceV3 } from '../../../../commonv3/api/RemoteResourceV3';
import { CoachPositionIndexView } from '../../../types/coachPositions';
import { CoachPositionApis, CoachPositionReservationApis } from '../../../apis/coachPositions';
import { CoachPositionId } from '../../../../adminv3/types/session';
import autobind from 'autobind-decorator';
import { CurrentUserState } from '../../../../mainv2/states/CurrentUserState';
import { SubmitState } from '../../../../commonv3/states/SubmitState';
import { GlobalStatesV3 } from '../../../../commonv3/states/GlobalStatesV3';
import { V3Routes } from '../../../routes';
import { TimesheetPageState } from './TimesheetPageState';

enum LocationType {
  ONLINE = 'online',
  OFFLINE = 'offline',
}

const LIST_OPTIONS = [
  { label: '全て', value: null },
  { label: 'オンライン', value: LocationType.ONLINE },
  { label: 'オフライン', value: LocationType.OFFLINE },
];

export class TimesheetPositionListState {
  userState: CurrentUserState = null;
  pageState: TimesheetPageState = null;
  locationTypeOptions = LIST_OPTIONS;
  selectedLocationType = null;
  selectedMonth = null;
  startsAt: Moment = null;
  endsAt: Moment = null;
  selectedSessionType: SessionType = null;
  selectedDate: ISODate = null;
  positions: RemoteResourceV3<CoachPositionIndexView[]> = RemoteResourceV3.loading();
  originalValues: ObservableMap<number, boolean> = observable.map();
  updates: ObservableMap<number, boolean> = observable.map();
  submitState = new SubmitState();

  constructor(userState, pageState) {
    makeObservable(this, {
      selectedLocationType: observable,
      selectedMonth: observable,
      startsAt: observable,
      endsAt: observable,
      selectedSessionType: observable,
      selectedDate: observable,
      positions: observable,
      originalValues: observable,
      updates: observable,
      submitState: observable,
      init: action.bound,
      setSelectedMonth: action.bound,
      setSelectedDate: action.bound,
      setSelectedSessionType: action.bound,
      setLocationType: action.bound,
      setAttendance: action.bound,
      hasUpdates: computed,
      filteredByType: computed,
      filteredPositions: computed,
      submit: action.bound,
    });

    this.userState = userState;
    this.pageState = pageState;
  }

  async init(type: SessionType) {
    this.selectedSessionType = type;
    this.startsAt = moment().hour(0);
    this.endsAt = moment().add(6, 'month').hour(0);
    this.selectedDate = null;
    this.originalValues.clear();
    this.updates.clear();
    const positions = await updateRemoteResourceV3(
      this.positions,
      (v) => (this.positions = v),
      () => CoachPositionApis.findAll(this.startsAt, this.endsAt)
    );
    positions.forEach((pos) => this.originalValues.set(pos.id, !!pos.user_id));
  }

  setSelectedMonth(month: Moment) {
    this.selectedMonth = month;
    this.selectedDate = null;
  }

  setSelectedDate(date) {
    if (this.selectedDate != date) {
      this.selectedDate = date;
    } else {
      this.selectedDate = null;
    }
  }

  setSelectedSessionType(type: SessionType) {
    this.selectedSessionType = type;
  }

  setLocationType(value: LocationType) {
    this.selectedLocationType = value;
  }

  setAttendance(id: CoachPositionId, value: boolean) {
    const pos = this.positions.getOrElse([]).find((p) => p.id === id);
    if (!pos) {
      return;
    }
    const originalValue = !!pos.user_id;
    if (originalValue != value) {
      this.updates.set(id, value);
    } else {
      this.updates.delete(id);
    }
  }

  @autobind
  getAttendance(id: CoachPositionId): boolean {
    if (this.updates.has(id)) {
      return this.updates.get(id);
    }
    return this.originalValues.get(id);
  }

  get hasUpdates() {
    return this.updates.size > 0;
  }

  get filteredByType(): CoachPositionIndexView[] {
    return this.positions.getOrElse([]).filter((r) => r.session.session_type === this.selectedSessionType);
  }

  get filteredPositions(): CoachPositionIndexView[] {
    return this.filteredByType
      .filter((r) => {
        return !this.selectedMonth || moment(r.session.starts_at).month() === this.selectedMonth.month();
      })
      .filter((r) => {
        return !this.selectedDate || moment(r.session.starts_at).format('YYYY-MM-DD') === this.selectedDate;
      })
      .filter(
        (r) =>
          !this.selectedLocationType ||
          (this.selectedLocationType === LocationType.ONLINE && !r.session.location) ||
          (this.selectedLocationType === LocationType.OFFLINE && r.session.location)
      );
  }

  submit() {
    this.submitState.operate(async () => {
      try {
        const updates = [...this.updates.entries()];
        for (let i = 0; i < updates.length; ++i) {
          const [key, value] = updates[i];
          if (value) {
            await CoachPositionReservationApis.create(key);
          } else {
            await CoachPositionReservationApis.delete(key);
          }
        }
        await this.pageState.init();
        GlobalStatesV3.routing.push(V3Routes.timesheetComplete());
      } catch (e) {
        GlobalStatesV3.routing.push(V3Routes.timesheetError());
      }
    });
  }
}
